import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CustomDropdown from './CustomDropdown'; // Import the CustomDropdown component

// --- utility functions ---
const getUniqueYears = (dates) => {
  if (!dates || dates.length === 0) return [];
  const years = new Set(dates.map(date => new Date(date).getFullYear()));
  return Array.from(years).sort((a, b) => b - a);
};

const getMonthName = (monthIndex) =>
  new Date(2000, monthIndex).toLocaleString('default', { month: 'long' });

const getDaysInMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0).getDate();

const formatDate = (year, monthIndex, day) => {
  const month = (monthIndex + 1).toString().padStart(2, '0');
  const d = day.toString().padStart(2, '0');
  return `${year}-${month}-${d}`;
};

// --- main component ---
const CustomCalendar = ({ onDateSelect, initialStartDate, initialEndDate }) => {
  const datesFromStorage = localStorage.getItem('calendarDates');
  const availableDates = datesFromStorage ? JSON.parse(datesFromStorage) : [];

  const availableDatesSet = useMemo(() => new Set(availableDates), [availableDates]);
  const allAvailableYears = useMemo(() => getUniqueYears(availableDates), [availableDates]);
  const firstAvailableDate = initialStartDate
    ? new Date(initialStartDate)
    : (availableDates[0] ? new Date(availableDates[0]) : new Date());

  const [currentDate, setCurrentDate] = useState(firstAvailableDate);
  const [startTemp, setStartTemp] = useState(initialStartDate);
  const [endTemp, setEndTemp] = useState(initialEndDate);
  const [availableMonthsForYear, setAvailableMonthsForYear] = useState([]);

  const nextMonthDate = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  }, [currentDate]);

  const monthsForYear = useCallback((year, allDates) => {
    const months = new Set();
    allDates.forEach(dateStr => {
      const date = new Date(dateStr);
      if (date.getFullYear() === year) {
        months.add(date.getMonth());
      }
    });
    return Array.from(months).sort((a, b) => a - b);
  }, []);

  useEffect(() => {
    const currentYear = currentDate.getFullYear();
    const sortedMonths = monthsForYear(currentYear, availableDates);
    setAvailableMonthsForYear(sortedMonths);

    if (!sortedMonths.includes(currentDate.getMonth()) && sortedMonths.length > 0) {
      setCurrentDate(new Date(currentYear, sortedMonths[0], 1));
    } else if (sortedMonths.length === 0 && allAvailableYears.length > 0) {
      const firstOverallDate = new Date(Array.from(availableDates).sort()[0]);
      setCurrentDate(new Date(firstOverallDate.getFullYear(), firstOverallDate.getMonth(), 1));
    } else if (availableDates.length === 0) {
      setCurrentDate(new Date());
    }
  }, [currentDate.getFullYear(), availableDates, allAvailableYears, monthsForYear]);

  useEffect(() => {
    setStartTemp(initialStartDate);
    setEndTemp(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // --- handlers ---
  const handleDayClick = (day, monthIndex, year) => {
    const formattedDate = formatDate(year, monthIndex, day);
    if (!availableDatesSet.has(formattedDate)) return;

    if (formattedDate === startTemp) {
      setStartTemp(null);
      setEndTemp(null);
      return;
    }
    if (formattedDate === endTemp) {
      setEndTemp(null);
      return;
    }

    if (!startTemp || (startTemp && endTemp)) {
      setStartTemp(formattedDate);
      setEndTemp(null);
    } else if (new Date(formattedDate) < new Date(startTemp)) {
      setEndTemp(startTemp);
      setStartTemp(formattedDate);
    } else {
      setEndTemp(formattedDate);
    }
  };

  const handleApply = () => {
    if (startTemp && !endTemp) {
      onDateSelect(startTemp, startTemp);
    } else {
      onDateSelect(startTemp, endTemp);
    }
  };

  const handleClear = () => {
    setStartTemp(null);
    setEndTemp(null);
    onDateSelect(null, null);
  };

  const handlePrev = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
  };

  const handleNext = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
  };

  const handleYearChange = (newYearValue) => {
    const newYear = parseInt(newYearValue, 10);
    const currentMonth = currentDate.getMonth();
    let targetMonth = currentMonth;
    const monthsForSelectedYear = monthsForYear(newYear, availableDates);

    if (!monthsForSelectedYear.includes(currentMonth) && monthsForSelectedYear.length > 0) {
      targetMonth = monthsForSelectedYear[0];
    }
    setCurrentDate(new Date(newYear, targetMonth, 1));
  };

  const handleMonthChange = (newMonthValue) => {
    const newMonth = parseInt(newMonthValue, 10);
    setCurrentDate(new Date(currentDate.getFullYear(), newMonth, 1));
  };

  const yearOptions = allAvailableYears.map(year => ({ label: String(year), value: year }));
  const monthOptions = availableMonthsForYear.map(monthIndex => ({
    label: getMonthName(monthIndex),
    value: monthIndex
  }));

  // --- rendering ---
  const renderMonthGrid = (date) => {
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const daysInMonth = getDaysInMonth(year, monthIndex);
    const firstDayOfMonth = new Date(year, monthIndex, 1).getDay();
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
      <div className="flex-1">
        <div className="grid grid-cols-7 gap-1 text-center font-[Archivo]">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
            <div key={day} className="text-xs font-medium text-gray-400">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 mt-1 font-[Archivo]">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="h-8"></div>
          ))}
          {daysArray.map(day => {
            const formattedDate = formatDate(year, monthIndex, day);
            const isAvailable = availableDatesSet.has(formattedDate);
            const isSelectedRange = startTemp && endTemp && new Date(formattedDate) >= new Date(startTemp) && new Date(formattedDate) <= new Date(endTemp);
            const isStart = formattedDate === startTemp;
            const isEnd = formattedDate === endTemp;
            const isDisabled = !isAvailable;

            return (
              <div
                key={day}
                onClick={() => isAvailable && handleDayClick(day, monthIndex, year)}
                className={`
                  relative flex items-center justify-center h-8 w-8 mx-auto text-sm rounded-full transition-colors
                  ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                  ${isSelectedRange && !isStart && !isEnd ? 'bg-blue-100 text-blue-800' : ''}
                  ${(isStart || isEnd) ? 'bg-blue-600 text-white font-bold' : ''}
                `}
              >
                {day}
                {isAvailable && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-green-500"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center p-4 mx-auto w-full max-w-sm sm:max-w-md lg:max-w-sm bg-white rounded-lg shadow-md border font-[Archivo]">
      {/* header navigation */}
      <div className="flex justify-between items-center w-full mb-4">
        <button onClick={handlePrev} className="mr-4 rounded-full hover:bg-gray-100">
          <FaChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
        <div className="flex flex-col sm:flex-row sm:gap-2 items-center">
          <CustomDropdown
            options={yearOptions}
            value={currentDate.getFullYear()}
            onChange={handleYearChange}
            placeholder="Select Year"
            buttonClass="min-w-[80px]"
          />
          <CustomDropdown
            options={monthOptions}
            value={currentDate.getMonth()}
            onChange={handleMonthChange}
            placeholder="Select Month"
            buttonClass="min-w-[100px]"
          />
        </div>
        <button onClick={handleNext} className="ml-4 rounded-full hover:bg-gray-100">
          <FaChevronRight className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {/* month grid */}
      <div className="w-full flex justify-center px-2">{renderMonthGrid(currentDate)}</div>

      {/* action buttons */}
      <div className="flex justify-end gap-2 mt-6 pt-3 border-t w-full">
        <button
          onClick={handleClear}
          className="px-4 py-2 text-sm rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300"
        >
          Clear
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 text-sm rounded-lg text-white bg-[#2573EB] hover:bg-[#1a5fcc]"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default CustomCalendar;
